import { ReactNode, useEffect, useState } from "react";
import NextLink from "next/link";
import {
  Text,
  Heading,
  Button,
  Link,
  Box,
  IconFa,
} from "@cruk/cruk-react-components";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";

import { useTracking } from "@fwa/src/hooks/useTracking";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";

import { HomePageSearch } from "@fwa/src/components/HomePageSearch";
import { BubbleCard } from "@fwa/src/components/BubbleCard";
import { HomePageImpactStatement } from "@fwa/src/components/HomePageImpactStatement";
import { HomePageImpactMoney } from "@fwa/src/components/HomePageImpactMoney";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/HomePage/styles.module.css";
import { useScrollPosition } from "@fwa/src/hooks/useScrollPosition";

const heroImage = "/assets/images/img/homepage_hero_hands-1024.jpeg";
const featureImageGivingPage =
  "/assets/images/img/homepage_features_giving_page_example.png";
const featureImageMsgBoard =
  "/assets/images/img/homepage_features_msg_board.jpg";
const featureImageRun = "/assets/images/img/homepage_features_fun_run.jpg";
const featureImageCarWash = "/assets/images/img/homepage_features_car_wash.jpg";
const faceBookLogo = "/assets/images/logos/fb-logo.png";
const stravaLogo = "/assets/images/logos/strava-logo-circle.png";
const fitBitLogo = "/assets/images/logos/fitbit-logo.png";
const impactStatement100 = "/assets/images/img/impact_statement_100.jpg";
const impactStatement250 = "/assets/images/img/impact_statement_250.jpg";
const impactStatement500 = "/assets/images/img/impact_statement_500.jpg";
const impactStatement1000 = "/assets/images/img/impact_statement_1000.jpg";
const impactStatement2500 = "/assets/images/img/impact_statement_2500.jpg";
const impactStatement5000 = "/assets/images/img/impact_statement_5000.jpg";

const circleSVGStringUrl = ({
  backgroundColor = "#ededed",
}: {
  backgroundColor?: string;
}) => {
  const calcColour = backgroundColor
    ? backgroundColor
    : `var(--clr-background-mid, #e6e6e6)`;
  const image = Buffer.from(
    `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 100 100"><circle class="a" cx="50" cy="50" r="50" fill="${calcColour}" /></svg>`,
    "binary",
  ).toString("base64");
  const backgroundUrl = `url("data:image/svg+xml;base64,${image}")`;
  return backgroundUrl;
};

const scrollToTarget = (targetId: string) => {
  document
    .getElementById(`${targetId}`)
    ?.scrollIntoView({ behavior: "smooth" });
};

export const CircleWrapper = ({
  children,
  backgroundColor,
  hasOverflow = false,
  maxWidth = "none",
}: {
  children?: ReactNode;
  backgroundColor?: string;
  hasOverflow?: boolean;
  maxWidth?: string;
}) => (
  <div
    className={styles["circle-wrapper"]}
    style={{
      backgroundImage: circleSVGStringUrl({ backgroundColor }),
      backgroundSize: hasOverflow ? "cover" : "inherit",
      maxWidth: maxWidth,
      maxHeight: maxWidth,
    }}
  >
    <div className={styles["circle-content-wrapper"]}>{children}</div>
  </div>
);

export function HomePage() {
  const { trackEventGtm } = useTracking();
  const [moneyItem, setMoneyItem] = useState<0 | 1 | 2 | 3 | 4 | 5 | 6>(0);
  const [brandName, setBrandName] = useBrandContext();

  // when impact statements mount, store their positions
  const [statementPositions, setStatementPositions] = useState<
    { number: number; top: number; bottom: number }[]
  >([]);

  // Track scroll position to update money item based on current statement in view
  useScrollPosition(
    ({
      currPos,
    }: {
      prevPos: { x: number; y: number };
      currPos: { x: number; y: number };
    }) => {
      const scrollY = currPos.y;
      const scrollMiddle = scrollY + window.innerHeight / 2;
      // Find the current statement based on scroll position
      const currentStatement = statementPositions.find((pos) => {
        const isStatementInMiddle =
          scrollMiddle >= pos.top && scrollMiddle < pos.bottom;
        return isStatementInMiddle;
      });

      if (currentStatement) {
        setMoneyItem(currentStatement.number as 0 | 1 | 2 | 3 | 4 | 5 | 6);
      } else {
        setMoneyItem(0);
      }
    },
    [statementPositions],
    null,
    true,
    100,
  );

  // useEffect to force brandContext on browser navigation
  useEffect(() => {
    if (brandName === "cruk") return;
    setBrandName("cruk");
  }, [brandName, setBrandName]);

  return (
    <div
      className={sharedStyles["content-max-width"]}
      data-component="homepage"
    >
      <div className={styles["hero-wrapper"]}>
        <img className={styles["hero-image"]} src={heroImage} alt="" />
        <div className={styles["hero-content-wrapper"]}>
          <CircleWrapper backgroundColor={`var(--clr-cruk-navy-700, #00007e)`}>
            <div
              className={[styles["bubble"], "color-props"].join(" ")}
              data-variation={0}
              data-is-hero={true}
              data-bg-color="secondary"
            />
            <div
              className={[styles["bubble"], "color-props"].join(" ")}
              data-variation={1}
              data-is-hero={true}
              data-bg-color="tertiary"
            />
            <div className={sharedStyles["column-center"]}>
              <div className={sharedStyles["row-center"]}>
                <Heading
                  h2
                  as="h1"
                  textColor="text-light"
                  textAlign="center"
                  marginTop="s"
                  padding="xxs"
                >
                  Fundraise online for Cancer Research UK
                </Heading>
              </div>
              <div className={styles["hero-btn-wrapper"]} data-component="hero">
                <NextLink
                  href="/signup?create=true"
                  onClick={() => {
                    trackEventGtm({
                      event: "create_fundraising_page_cta",
                      cta: "hero",
                    });
                  }}
                  data-cta-type="create-page"
                >
                  <Button appearance="primary" as="span">
                    Create a Giving Page
                  </Button>
                </NextLink>
              </div>
            </div>
          </CircleWrapper>
        </div>
      </div>
      <div
        className={[
          "spacing-props",
          sharedStyles["content-wrapper-responsive"],
        ].join(" ")}
        data-margin-bottom="xxl"
      >
        <Heading
          h4
          as="h2"
          textAlign="center"
          textColor="primary"
          paddingBottom="l"
        >
          Join thousands of supporters raising money for life-saving work now.
        </Heading>
      </div>
      <HomePageSearch />
      {/* ---------------------- Features section -------------------------------- */}
      <div
        className={["spacing-props", "color-props"].join(" ")}
        data-padding-bottom="m"
        data-margin-top="s"
        data-bg-color="background-light"
      >
        <div className={styles["top-bubble-mobile-spacer"]}>
          <div className={styles["circle-wrapper-fixed-width"]}>
            <div className={sharedStyles["column-center"]}>
              <div className={sharedStyles["row-center"]}>
                <Heading
                  h2
                  textAlign="center"
                  textColor="primary"
                  paddingHorizontal="s"
                >
                  {`We're here to help you fundraise`}
                </Heading>
              </div>
              <Button
                className="component-button"
                aria-label="scroll to giving page information section"
                onClick={() => scrollToTarget("givingPageSection")}
                style={{
                  width: "4rem",
                  height: "4rem",
                  backgroundColor: "var(--clr-cruk-magenta-600, #ff0087)",
                  borderRadius: "100%",
                  margin: "0 auto",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div>
                  <IconFa
                    faIcon={faArrowDown}
                    size="3rem"
                    color={`var(--clr-cruk-white, #ffffff)`}
                  />
                </div>
              </Button>
            </div>
          </div>
        </div>
        <div className={styles["section-wrapper"]}>
          <Box id="givingPageSection" marginTop="m">
            <BubbleCard
              imageRight={false}
              variation={5}
              thirdBubbleColor={`var(--clr-cruk-grey-300, #c6c6c6)`}
              imageChild={
                <div className={styles["giving-page-demo-image-wrapper"]}>
                  <img src={featureImageGivingPage} alt="Giving page example" />
                </div>
              }
            >
              <div className={styles["bubble-text-wrapper"]}>
                <Heading h2 as="h3">
                  Get a head start with a Giving Page
                </Heading>
                <Text>
                  {`When you sign up to fundraise with Cancer Research UK, we
                  automatically create a Giving Page. So you can start
                  fundraising as soon as you're ready. On your Giving Page, you
                  can share your story, fundraising target, progress and much
                  more.`}
                </Text>
              </div>
            </BubbleCard>
          </Box>

          <BubbleCard
            imageUrl={featureImageMsgBoard}
            alt=""
            imageRight
            variation={2}
          >
            <Box>
              <div className={styles["bubble-text-wrapper"]}>
                <Heading h2 as="h3">
                  Make every pound count
                </Heading>
                <Text>
                  {`Fundraising directly through Cancer Research UK means we won't
                  have to pay other charitable platform fees so a larger chunk
                  of the money you raise will go directly to our amazing
                  research.`}
                </Text>
              </div>
            </Box>
          </BubbleCard>

          <BubbleCard
            imageRight={false}
            variation={6}
            thirdBubbleColor={`var(--clr-cruk-grey-300, #c6c6c6)`}
            imageChild={
              <div className={styles["connect-app-images-grid"]}>
                <div className={styles["facebook-logo-wrapper"]}>
                  <img src={faceBookLogo} alt="Facebook logo" />
                </div>
                <div className={styles["strava-logo-wrapper"]}>
                  <img src={stravaLogo} alt="Strava logo" />
                </div>
                <div className={styles["fitbit-logo-wrapper"]}>
                  <img src={fitBitLogo} alt="Fit bit Logo" />
                </div>
              </div>
            }
          >
            <Box>
              <div className={styles["bubble-text-wrapper"]}>
                <Heading h2 as="h3">
                  Connect to your favourite apps
                </Heading>
                <Text>
                  {`You can connect your Giving Page with social and fitness
                  accounts to share your progress with friends and family.
                  People are more likely to donate when they can see every mile,
                  step or cycle.`}
                </Text>
              </div>
            </Box>
          </BubbleCard>

          <BubbleCard
            imageUrl={featureImageRun}
            alt=""
            imageRight
            variation={3}
          >
            <Box>
              <div className={styles["bubble-text-wrapper"]}>
                <Heading h2 as="h3">
                  Fundraise together and beat cancer together
                </Heading>
                <Text>
                  {`You can create a team for your fundraising challenge and smash
                  those targets as a squad. No team is too big or too small to
                  show cancer who's boss. You can do this easily from your
                  Giving Page.`}
                </Text>
              </div>
            </Box>
          </BubbleCard>

          <BubbleCard
            imageUrl={featureImageCarWash}
            alt=""
            imageRight={false}
            variation={4}
          >
            <Box>
              <div className={styles["bubble-text-wrapper"]}>
                <Heading h2 as="h3">
                  Share your fundraising journey
                </Heading>
                <Text>
                  {`Personalise your Giving Page with your story and photos to
                  tell everyone why you're fundraising for Cancer Research UK.
                  People who share their story raise more money towards beating
                  their fundraising target.`}
                </Text>
              </div>
            </Box>
          </BubbleCard>
        </div>
      </div>
      <Box
        backgroundColor="background-light"
        paddingVertical="s"
        paddingHorizontal="none"
      >
        <CircleWrapper
          backgroundColor={`var(--clr-cruk-navy-100, #e5e5ff)`}
          data-component="middle-ctas"
        >
          <Text textAlign="center" textSize="l">
            Ready to fundraise?
          </Text>
          <Box marginBottom="xs">
            <div className={sharedStyles["row-center"]}>
              <NextLink
                href="/signup?create=true"
                onClick={() => {
                  trackEventGtm({
                    event: "create_fundraising_page_cta",
                    cta: "fundraise now - middle",
                  });
                }}
                data-cta-type="create-page"
              >
                <Button appearance="primary" as="span">
                  Create a Giving Page
                </Button>
              </NextLink>
            </div>
          </Box>
          <Text textAlign="center" textSize="l">
            Need a little more support?
          </Text>
          <div className={sharedStyles["row-center"]}>
            <Link
              href="https://www.cancerresearchuk.org/get-involved/do-your-own-fundraising"
              textAlign="center"
              onClick={() => {
                trackEventGtm({
                  event: "create_fundraising_page_cta",
                  cta: "get_inspiration_middle",
                });
              }}
              data-cta-type="link-request-inspiration"
            >
              <Button as="span" appearance="secondary">
                Get inspiration or advice
              </Button>
            </Link>
          </div>
        </CircleWrapper>
      </Box>
      <Box>
        <CircleWrapper
          backgroundColor={`var(--clr-primary, #00007e)`}
          hasOverflow
          maxWidth="27em"
        >
          <div
            className={[
              "spacing-props",
              sharedStyles["content-wrapper-responsive"],
            ].join(" ")}
          >
            <div className={styles["impact-jump-to-button-copy-wrapper"]}>
              <div className={sharedStyles["row-center"]}>
                <Text
                  textSize="xl" // the design is larger (40px) than this component allows.
                  textAlign="left"
                  textColor="text-on-primary"
                  textFontFamily="headings"
                >
                  Every year, two million amazing fundraisers raise over £40m
                </Text>
              </div>
              <div className={sharedStyles["row-center"]}>
                <Text textAlign="left" textColor="text-on-primary">
                  See how your fundraising could make a difference to our
                  life-saving work
                </Text>
              </div>
              <Box paddingTop="s">
                <Button
                  aria-label="scroll to giving page information section"
                  onClick={() => scrollToTarget("impact-statements")}
                  style={{
                    width: "4rem",
                    height: "4rem",
                    backgroundColor: "var(--clr-cruk-magenta-600, #ff0087)",
                    borderRadius: "100%",
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div>
                    <IconFa
                      faIcon={faArrowDown}
                      size="3rem"
                      color="text-on-primary"
                    />
                  </div>
                </Button>
              </Box>
            </div>
          </div>
        </CircleWrapper>
      </Box>

      {/* ---------------------- Impact statements section -------------------------------- */}
      <div className={styles["section-wrapper"]} id="impact-statements">
        <HomePageImpactStatement
          number={1}
          setStatementPositions={setStatementPositions}
        >
          <BubbleCard
            imageUrl={impactStatement100}
            alt=""
            imageRight
            variation={7}
            isAnimated={false}
          >
            <div className={sharedStyles["row"]}>
              <div className={styles["impact-statement-copy-wrapper"]}>
                <div className={styles["bubble-text-wrapper"]}>
                  <Heading h2 as="h3">
                    Spotting cancer earlier
                  </Heading>
                  <Text>
                    {`£100 could pay for a patient's cancer biopsy where a tiny
                    amount of a lump or tumour is removed and sent to be studied
                    in a lab. Early diagnosis is essential to getting the right
                    treatment to patients.`}
                  </Text>
                </div>
              </div>
            </div>
          </BubbleCard>
        </HomePageImpactStatement>

        <HomePageImpactStatement
          number={2}
          setStatementPositions={setStatementPositions}
        >
          <BubbleCard
            imageUrl={impactStatement250}
            thirdBubbleColor={`var(--clr-cruk-grey-300, #c6c6c6)`}
            alt=""
            imageRight
            variation={8}
            isAnimated={false}
          >
            <div className={sharedStyles["row"]}>
              <div className={styles["impact-statement-copy-wrapper"]}>
                <div className={styles["bubble-text-wrapper"]}>
                  <Heading h2 as="h3">
                    {`Finding cancer's weakness`}
                  </Heading>
                  <Text>
                    {`£250 could buy a special device used to spin blood samples
                    until different types of blood cells separate. Our
                    scientists can then study the different types of cancerous
                    cells to find out more about them. So we can stop cancer in
                    its tracks.`}
                  </Text>
                </div>
              </div>
            </div>
          </BubbleCard>
        </HomePageImpactStatement>

        <HomePageImpactStatement
          number={3}
          setStatementPositions={setStatementPositions}
        >
          <BubbleCard
            imageUrl={impactStatement500}
            alt=""
            imageRight
            variation={9}
            isAnimated={false}
          >
            <div className={sharedStyles["row"]}>
              <div className={styles["impact-statement-copy-wrapper"]}>
                <div>
                  <Heading h2 as="h3">
                    Providing cancer support
                  </Heading>
                  <Text>
                    {`£500 could run our online forum, Cancer Chat for a day. It's
                    a safe space open to everyone to discuss any aspect of the
                    disease. Cancer is unpredictable, so let's give people
                    affected by cancer the support they need at every turn.`}
                  </Text>
                </div>
              </div>
            </div>
          </BubbleCard>
        </HomePageImpactStatement>

        <HomePageImpactStatement
          number={4}
          setStatementPositions={setStatementPositions}
        >
          <BubbleCard
            imageUrl={impactStatement1000}
            alt=""
            imageRight
            variation={10}
            isAnimated={false}
          >
            <div className={sharedStyles["row"]}>
              <div className={styles["impact-statement-copy-wrapper"]}>
                <div className={styles["bubble-text-wrapper"]}>
                  <Heading h2 as="h3">
                    Hunting innovative discoveries
                  </Heading>
                  <Text>
                    {`£1,000 could buy imaging software that our scientists can use
                    to film cells in real time. This could help to test
                    potentially life-saving treatments and find the most
                    promising new cancer drugs.`}
                  </Text>
                </div>
              </div>
            </div>
          </BubbleCard>
        </HomePageImpactStatement>

        <HomePageImpactStatement
          number={5}
          setStatementPositions={setStatementPositions}
        >
          <BubbleCard
            imageUrl={impactStatement2500}
            alt=""
            imageRight
            variation={11}
            isAnimated={false}
          >
            <div className={sharedStyles["row"]}>
              <div className={styles["impact-statement-copy-wrapper"]}>
                <div className={styles["bubble-text-wrapper"]}>
                  <Heading h2 as="h3">
                    Delivering treatments
                  </Heading>
                  <Text>
                    {`£2,500 could fund the PETReA trial for five days. This trial
                    aims to help doctors decide how best to treat lymphoma as
                    not all patients can take the same drug. Everyone deserves
                    kind cancer treatments that won't impact their long-term
                    health.`}
                  </Text>
                </div>
              </div>
            </div>
          </BubbleCard>
        </HomePageImpactStatement>

        <HomePageImpactStatement
          number={6}
          setStatementPositions={setStatementPositions}
        >
          <BubbleCard
            imageUrl={impactStatement5000}
            alt=""
            imageRight
            variation={12}
            isAnimated={false}
          >
            <div className={sharedStyles["row"]}>
              <div className={styles["impact-statement-copy-wrapper"]}>
                <div className={styles["bubble-text-wrapper"]}>
                  <Heading h2 as="h3">
                    Keeping cancer in remission
                  </Heading>
                  <Text>
                    {`£5,000 could fund the Add-Aspirin trial for five days. This
                    clinical trial is investigating whether aspirin could stop
                    cancer coming back after treatment. If successful, aspirin
                    would be a cheap and readily available intervention that
                    could save lives.`}
                  </Text>
                </div>
              </div>
            </div>
          </BubbleCard>
        </HomePageImpactStatement>
      </div>
      {/* This exists just so the last elememt before is selected more easily */}
      <HomePageImpactStatement number={7}>
        <Box
          backgroundColor="background-light"
          paddingTop="s"
          paddingHorizontal="none"
          paddingBottom="m"
        >
          <CircleWrapper
            backgroundColor={`var(--clr-navy-100, #e5e5ff)`}
            data-component="bottom-ctas"
          >
            <Text textAlign="center" textSize="l">
              Ready to fundraise?
            </Text>
            <Box marginBottom="xs">
              <div className={sharedStyles["row-center"]}>
                <NextLink
                  href="/signup?create=true"
                  onClick={() => {
                    trackEventGtm({
                      event: "create_fundraising_page_cta",
                      cta: "fundraise now - bottom",
                    });
                  }}
                  data-cta-type="create-page"
                >
                  <Button appearance="primary" as="span">
                    Create a Giving Page
                  </Button>
                </NextLink>
              </div>
            </Box>
            <Text textAlign="center" textSize="l">
              Need a little more support?
            </Text>
            <div className={sharedStyles["row-center"]}>
              <Link
                href="https://www.cancerresearchuk.org/get-involved/do-your-own-fundraising"
                textAlign="center"
                data-cta-type="link-request-inspiration"
              >
                <Button
                  as="span"
                  appearance="secondary"
                  onClick={() => {
                    trackEventGtm({
                      event: "create_fundraising_page_cta",
                      cta: "get_inspiration_bottom",
                    });
                  }}
                >
                  Get inspiration or advice
                </Button>
              </Link>
            </div>
          </CircleWrapper>
        </Box>
      </HomePageImpactStatement>
      <HomePageImpactMoney selectedNumber={moneyItem} />
    </div>
  );
}

export default HomePage;
